// Cached reads for each screen. Keys: me, auth, edition:<n|today>, play:<n>, board:<n>:<scope>,
// stats, thread:<n>, sbinfo:<n|today>, sbrun:<n>, sbboard:<n>:<scope>, challenge:<code>, edchallenges:<n>, mychallenges, chat:<code>, rivals,
// rivalry:<id>, achievements, profile:<handle>, admin:overview, admin:sources, admin:analytics:<days>, admin:slowburn:<days>, admin:feedback:<status>.
import type { MyAchievements, PlayerProfile, AdminAnalytics, AdminAvatars, AdminOverview, AdminReminders, AdminSlowBurn, AdminSources, AnalyticsDays, AuthConfig, Challenge, ChallengeList, ChallengeThread, CommentThread, EditionInfo, FeedbackPage, FeedbackStatus, Leaderboard, LeaderboardScope, Me, MyChallenges, Play, Rivalry, Rivals, SlowBurnBoard, SlowBurnInfo, SlowBurnRun, SlowBurnScope, Stats } from '@wbwl/contract'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { api } from './api.ts'
import { firstTouchQuery, firstTouchSent } from './first-touch.ts'
import { getResource, prefetch, setResource, useResource } from './store.ts'

export const useMe = () => useResource<Me>('me', useCallback(async () => {
  const me = await api.get<Me>(`/me${firstTouchQuery()}`)
  firstTouchSent()
  return me
}, []))
export const useAuthConfig = () => useResource<AuthConfig>('auth', useCallback(() => api.get<AuthConfig>('/auth/config'), []))

/** Today's edition, or a past one by number. Today's is also cached under its number. */
const fetchEdition = (n: number | 'today') => async () => {
  const e = await api.get<EditionInfo>(n === 'today' ? '/editions/today' : `/editions/${n}`)
  if (n === 'today') setResource(`edition:${e.number}`, e)
  return e
}
export function useEdition(n: number | 'today' | null) {
  return useResource<EditionInfo>(n === null ? null : `edition:${n}`, useMemo(() => fetchEdition(n ?? 'today'), [n]))
}
/** Today's edition, asked for alongside /me on a cold open (the route waits for /me; it needs no session). */
export const prefetchToday = () => prefetch('edition:today', fetchEdition('today'))

/**
 * Whether an edition's day is over (its `nextAt` has passed). Checked again just after that moment
 * and whenever the page comes back into view, since a sleeping device can hold timers back.
 */
export function useDayOver(nextAt: string | undefined): boolean {
  const [, recheck] = useState(0)
  useEffect(() => {
    if (!nextAt) return
    const on = () => recheck((n) => n + 1)
    const left = Date.parse(nextAt) - Date.now()
    // A moment past midnight, so the server has turned the day over too.
    const timer = left > 0 ? setTimeout(on, Math.min(left + 1000, 2 ** 31 - 1)) : undefined
    document.addEventListener('visibilitychange', on)
    window.addEventListener('focus', on)
    return () => {
      clearTimeout(timer)
      document.removeEventListener('visibilitychange', on)
      window.removeEventListener('focus', on)
    }
  }, [nextAt])
  return nextAt !== undefined && Date.now() >= Date.parse(nextAt)
}

export const usePlay = (n: number | null) =>
  useResource<Play>(n === null ? null : `play:${n}`, useCallback(() => api.get<Play>(`/plays/${n}`), [n]))

export const useLeaderboard = (n: number | null, scope: LeaderboardScope) =>
  useResource<Leaderboard>(n === null ? null : `board:${n}:${scope}`, useCallback(() => api.get<Leaderboard>(`/leaderboards/${n}?scope=${scope}`), [n, scope]))

/** Today's Slow Burn, or a past one by number. Today's is also cached under its number. */
export function useSlowBurn(n: number | 'today' | null) {
  return useResource<SlowBurnInfo>(n === null ? null : `sbinfo:${n}`, useCallback(async () => {
    const info = await api.get<SlowBurnInfo>(`/slow-burn/${n}`)
    if (n === 'today') setResource(`sbinfo:${info.number}`, info)
    return info
  }, [n]))
}

export const useRun = (n: number | null) =>
  useResource<SlowBurnRun>(n === null ? null : `sbrun:${n}`, useCallback(() => api.get<SlowBurnRun>(`/slow-burn/${n}/run`), [n]))

export const useSlowBurnBoard = (n: number | null, scope: SlowBurnScope) =>
  useResource<SlowBurnBoard>(n === null ? null : `sbboard:${n}:${scope}`, useCallback(() => api.get<SlowBurnBoard>(`/slow-burn/${n}/board?scope=${scope}`), [n, scope]), FRESH)

/** Your stats; `enabled` false skips the request. */
export const useStats = (enabled = true) => useResource<Stats>(enabled ? 'stats' : null, useCallback(() => api.get<Stats>('/me/stats'), []))

// Other players join, finish and post in challenges and discussions, so these refetch whenever a
// screen opens.
const FRESH = { revalidate: true }

export const useThread = (n: number | null) =>
  useResource<CommentThread>(n === null ? null : `thread:${n}`, useCallback(() => api.get<CommentThread>(`/editions/${n}/comments`), [n]), FRESH)

/** Opening a challenge also marks its results seen. */
export const useChallenge = (code: string | null) =>
  useResource<Challenge>(code === null ? null : `challenge:${code}`, useCallback(() => api.get<Challenge>(`/challenges/${code}`), [code]), FRESH)

/** The challenges you're in or invited to for an edition. */
export const useEditionChallenges = (n: number | null) =>
  useResource<ChallengeList>(n === null ? null : `edchallenges:${n}`, useCallback(() => api.get<ChallengeList>(`/editions/${n}/challenges`), [n]), FRESH)

/**
 * The last week's challenges you're in or invited to, by edition: the Rivals tab, its dot and
 * Today's strip. The tab bar's dot passes `revalidate` false, reading what the pages last fetched.
 */
export const useMyChallenges = (revalidate = true) =>
  useResource<MyChallenges>('mychallenges', useCallback(() => api.get<MyChallenges>('/me/challenges'), []), { revalidate })

export const useChallengeThread = (code: string | null) =>
  useResource<ChallengeThread>(code === null ? null : `chat:${code}`, useCallback(() => api.get<ChallengeThread>(`/challenges/${code}/comments`), [code]), FRESH)

/** Your head-to-head records; `enabled` false skips the request (a closed sheet). */
export const useRivals = (enabled = true) =>
  useResource<Rivals>(enabled ? 'rivals' : null, useCallback(() => api.get<Rivals>('/me/rivals'), []), FRESH)

export const useRivalry = (id: string | null) =>
  useResource<Rivalry>(id === null ? null : `rivalry:${id}`, useCallback(() => api.get<Rivalry>(`/rivals/${id}`), [id]), FRESH)

/** Your achievements, by category (a guest's are unclaimed); `enabled` false skips the request. */
export const useMyAchievements = (enabled = true) =>
  useResource<MyAchievements>(enabled ? 'achievements' : null, useCallback(() => api.get<MyAchievements>('/me/achievements'), []))

/** A member's public profile by its handle (only the code part finds them). */
export const usePlayerProfile = (handle: string | null) =>
  useResource<PlayerProfile>(handle === null ? null : `profile:${handle}`, useCallback(() => api.get<PlayerProfile>(`/players/${handle}`), [handle]), FRESH)

export const useAdminOverview = () => useResource<AdminOverview>('admin:overview', useCallback(() => api.get<AdminOverview>('/admin/overview'), []))

export const useAdminAvatars = () => useResource<AdminAvatars>('admin:avatars', useCallback(() => api.get<AdminAvatars>('/admin/avatars'), []), { revalidate: true })

export const useAdminAnalytics = (days: AnalyticsDays) =>
  useResource<AdminAnalytics>(`admin:analytics:${days}`, useCallback(() => api.get<AdminAnalytics>(`/admin/analytics?days=${days}`), [days]), FRESH)

export const useAdminSlowBurn = (days: AnalyticsDays) =>
  useResource<AdminSlowBurn>(`admin:slowburn:${days}`, useCallback(() => api.get<AdminSlowBurn>(`/admin/slow-burn?days=${days}`), [days]), FRESH)

export const useAdminReminders = () => useResource<AdminReminders>('admin:reminders', useCallback(() => api.get<AdminReminders>('/admin/reminders'), []), { revalidate: true })

export const useAdminSources = () => useResource<AdminSources>('admin:sources', useCallback(() => api.get<AdminSources>('/admin/sources'), []))

/** The first page of feedback with a status; the admin page appends later pages to it. */
export const useAdminFeedback = (status: FeedbackStatus) =>
  useResource<FeedbackPage>(`admin:feedback:${status}`, useCallback(() => api.get<FeedbackPage>(`/admin/feedback?status=${status}`), [status]))

export const cachedPlay = (n: number) => getResource<Play>(`play:${n}`)
